import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import Toolbar from '@mui/material/Toolbar';
import type { } from '@mui/material/themeCssVarsAugmentation';
import { matchPath, useLocation } from 'react-router';
import DashboardSidebarContext from '../../context/DashboardSidebarContext';
import { DRAWER_WIDTH, MINI_DRAWER_WIDTH } from '../../constants';
import DashboardSidebarPageItem from './DashboardSidebarPageItem';
import DashboardSidebarHeaderItem from './DashboardSidebarHeaderItem';
import DashboardSidebarDividerItem from './DashboardSidebarDividerItem';
import {
  getDrawerSxTransitionMixin,
  getDrawerWidthTransitionMixin,
} from '../../mixins';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { BlenderOutlined, FactoryOutlined, GroupOutlined, LogoutOutlined, PersonOutline, ScienceOutlined, ShoppingCartOutlined, WarehouseOutlined, PaidOutlined, PaymentsOutlined, SellOutlined } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';

export interface DashboardSidebarProps {
  expanded?: boolean;
  setExpanded: (expanded: boolean) => void;
  disableCollapsibleSidebar?: boolean;
  container?: Element;
}

export default function DashboardSidebar({
  expanded = true,
  setExpanded,
  disableCollapsibleSidebar = false,
  container,
}: DashboardSidebarProps) {
  const theme = useTheme();

  const { pathname } = useLocation();

  const isOverSmViewport = useMediaQuery(theme.breakpoints.up('sm'));
  const isOverMdViewport = useMediaQuery(theme.breakpoints.up('md'));

  const { user } = useAuth();

  const [isFullyExpanded, setIsFullyExpanded] = useState(expanded);
  const [isFullyCollapsed, setIsFullyCollapsed] = useState(!expanded);

  useEffect(() => {
    if (expanded) {
      const drawerWidthTransitionTimeout = setTimeout(() => {
        setIsFullyExpanded(true);
      }, theme.transitions.duration.enteringScreen);

      return () => clearTimeout(drawerWidthTransitionTimeout);
    }

    setIsFullyExpanded(false);

    return () => { };
  }, [expanded, theme.transitions.duration.enteringScreen]);

  useEffect(() => {
    if (!expanded) {
      const drawerWidthTransitionTimeout = setTimeout(() => {
        setIsFullyCollapsed(true);
      }, theme.transitions.duration.leavingScreen);

      return () => clearTimeout(drawerWidthTransitionTimeout);
    }

    setIsFullyCollapsed(false);

    return () => { };
  }, [expanded, theme.transitions.duration.leavingScreen]);

  const mini = !disableCollapsibleSidebar && !expanded;

  const handleSetSidebarExpanded = useCallback(
    (newExpanded: boolean) => () => {
      setExpanded(newExpanded);
    },
    [setExpanded],
  );

  const handlePageItemClick = useCallback(
    (itemId: string, hasNestedNavigation: boolean) => {
      if (hasNestedNavigation && !mini) {
        /*
        setExpandedItemIds((previousValue) =>
          previousValue.includes(itemId)
            ? previousValue.filter(
                (previousValueItemId) => previousValueItemId !== itemId,
              )
            : [...previousValue, itemId],
        );
        */
      } else if (!isOverSmViewport && !hasNestedNavigation) {
        setExpanded(false);
      }
    },
    [mini, setExpanded, isOverSmViewport],
  );

  const hasDrawerTransitions =
    isOverSmViewport && (!disableCollapsibleSidebar || isOverMdViewport);

  const getDrawerContent = useCallback(
    (viewport: 'phone' | 'tablet' | 'desktop') => (
      <Fragment>
        <Toolbar />
        <Box
          component="nav"
          aria-label={`${viewport.charAt(0).toUpperCase()}${viewport.slice(1)}`}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            overflow: 'auto',
            scrollbarGutter: mini ? 'stable' : 'auto',
            overflowX: 'hidden',
            pt: !mini ? 0 : 2,
            ...(hasDrawerTransitions
              ? getDrawerSxTransitionMixin(isFullyExpanded, 'padding')
              : {}),
          }}
        >
          <List
            dense
            sx={{
              padding: mini ? 0 : 0.5,
              mb: 4,
              width: mini ? MINI_DRAWER_WIDTH : 'auto',
            }}
          >
            <DashboardSidebarHeaderItem>Main</DashboardSidebarHeaderItem>
            <DashboardSidebarPageItem
              id="items"
              title="Items"
              icon={<ScienceOutlined />}
              href="/dashboard/items"
              selected={!!matchPath('/dashboard/items/*', pathname) || pathname === '/'}
            />
            <DashboardSidebarPageItem
              id="purchaseOrders"
              title="Purchase Orders"
              icon={<ShoppingCartOutlined />}
              href="/dashboard/purchase-orders"
              selected={!!matchPath('/dashboard/purchase-orders/*', pathname) || pathname === '/'}
            />
            <DashboardSidebarPageItem
              id="contacts"
              title="Contacts"
              icon={<PersonOutline />}
              href="/dashboard/contacts"
              selected={!!matchPath('/dashboard/contacts/*', pathname) || pathname === '/'}
            />
            <DashboardSidebarPageItem
              id="vendors"
              title="Vendors"
              icon={<FactoryOutlined />}
              href="/dashboard/vendors"
              selected={!!matchPath('/dashboard/vendors/*', pathname) || pathname === '/'}
            />
            <DashboardSidebarPageItem
              id="customers"
              title="Customers"
              icon={<GroupOutlined />}
              href="/dashboard/customers"
              selected={!!matchPath('/dashboard/customers/*', pathname) || pathname === '/'}
            />
            <DashboardSidebarPageItem
              id="prodcuts"
              title="Products"
              icon={<BlenderOutlined />}
              href="/dashboard/products"
              selected={!!matchPath('/dashboard/products/*', pathname) || pathname === '/'}
            />
            <DashboardSidebarPageItem
              id="batches"
              title="Batches"
              icon={<WarehouseOutlined />}
              href="/dashboard/batches"
              selected={!!matchPath('/dashboard/batches/*', pathname) || pathname === '/'}
            />
            <DashboardSidebarPageItem
              id="orders"
              title="Online Orders"
              icon={<SellOutlined />}
              href="/dashboard/orders"
              selected={!!matchPath('/dashboard/orders/*', pathname) || pathname === '/'}
            />
            <DashboardSidebarPageItem
              id="overheadCosts"
              title="Overhead Costs"
              icon={<PaymentsOutlined />}
              href="/dashboard/overhead-costs"
              selected={!!matchPath('/dashboard/overhead-costs/*', pathname) || pathname === '/'}
            />
            <DashboardSidebarPageItem
              id="consumables"
              title="Consumables"
              icon={<PaidOutlined />}
              href="/dashboard/consumables"
              selected={!!matchPath('/dashboard/consumables/*', pathname) || pathname === '/'}
            />
            {user ?
              <>
                <DashboardSidebarDividerItem />
                <DashboardSidebarHeaderItem>{user.firstName} {user.lastName[0]}.</DashboardSidebarHeaderItem>
                <DashboardSidebarPageItem
                  id="logout"
                  title="Log Out"
                  icon={<LogoutOutlined />}
                  href={`${process.env.REACT_APP_API_URL}logout`}
                />
              </> :
              <></>
            }
          </List>
        </Box>
      </Fragment >
    ),
    [mini, hasDrawerTransitions, isFullyExpanded, pathname, user],
  );

  const getDrawerSharedSx = useCallback(
    (isTemporary: boolean) => {
      const drawerWidth = mini ? MINI_DRAWER_WIDTH : DRAWER_WIDTH;

      return {
        displayPrint: 'none',
        width: drawerWidth,
        flexShrink: 0,
        ...getDrawerWidthTransitionMixin(expanded),
        ...(isTemporary ? { position: 'absolute' } : {}),
        [`& .MuiDrawer-paper`]: {
          position: 'absolute',
          width: drawerWidth,
          boxSizing: 'border-box',
          backgroundImage: 'none',
          ...getDrawerWidthTransitionMixin(expanded),
        },
      };
    },
    [expanded, mini],
  );

  const sidebarContextValue = useMemo(() => {
    return {
      onPageItemClick: handlePageItemClick,
      mini,
      fullyExpanded: isFullyExpanded,
      fullyCollapsed: isFullyCollapsed,
      hasDrawerTransitions,
    };
  }, [
    handlePageItemClick,
    mini,
    isFullyExpanded,
    isFullyCollapsed,
    hasDrawerTransitions,
  ]);

  return (
    <DashboardSidebarContext.Provider value={sidebarContextValue}>
      <Drawer
        container={container}
        variant="temporary"
        open={expanded}
        onClose={handleSetSidebarExpanded(false)}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: {
            xs: 'block',
            sm: disableCollapsibleSidebar ? 'block' : 'none',
            md: 'none',
          },
          ...getDrawerSharedSx(true),
        }}
      >
        {getDrawerContent('phone')}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: {
            xs: 'none',
            sm: disableCollapsibleSidebar ? 'none' : 'block',
            md: 'none',
          },
          ...getDrawerSharedSx(false),
        }}
      >
        {getDrawerContent('tablet')}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          minHeight: '750px',
          display: { xs: 'none', md: 'block' },
          ...getDrawerSharedSx(false),
        }}
      >
        {getDrawerContent('desktop')}
      </Drawer>
    </DashboardSidebarContext.Provider>
  );
}
